/**
 * The 24 categories every brand and creator post is sorted into. Posts carry the slug
 * (`content_info.content_category`); the marketer dashboard's content insights compare how each
 * category performs.
 */
export const CONTENT_CATEGORIES = [
  {
    slug: 'automotive',
    name: 'Automotive',
    description: 'Cars, trucks, driving, and car culture.',
  },
  {
    slug: 'beauty_grooming',
    name: 'Beauty & Grooming',
    description: 'Skincare, hair, makeup, grooming, fragrance, self-care routines.',
  },
  {
    slug: 'community_faith_causes',
    name: 'Community, Faith & Causes',
    description: 'Giving back, faith, and causes.',
  },
  {
    slug: 'education_career',
    name: 'Education & Career',
    description: 'School, studying, and career or business building.',
  },
  {
    slug: 'entertainment_pop_culture',
    name: 'Entertainment & Pop Culture',
    description:
      'Reactions and commentary on movies, TV, music releases, celebrities, viral moments, and trends.',
  },
  {
    slug: 'family_relationships',
    name: 'Family & Relationships',
    description: 'Family, partner, kids, parents, siblings, or close friends as the subject.',
  },
  {
    slug: 'fashion_style',
    name: 'Fashion & Style',
    description: 'Clothing, outfits, sneakers, and accessories as the subject.',
  },
  {
    slug: 'food_dining',
    name: 'Food & Dining',
    description: 'Food for enjoyment: restaurants, cooking, recipes, and food reviews.',
  },
  {
    slug: 'gaming',
    name: 'Gaming',
    description: 'Video games, streaming, esports, and gaming gear.',
  },
  {
    slug: 'humor_comedy',
    name: 'Humor & Comedy',
    description: 'Content whose purpose is to make you laugh.',
  },
  {
    slug: 'lifestyle',
    name: 'Lifestyle',
    description: 'Everyday life away from sport, where no other category fits.',
  },
  {
    slug: 'money_finance',
    name: 'Money & Finance',
    description: 'Budgeting, saving, investing, banking, and NIL earnings.',
  },
  {
    slug: 'motivation_mindset',
    name: 'Motivation & Mindset',
    description: 'Content designed to inspire: discipline, goals, adversity, work ethic.',
  },
  {
    slug: 'music_dance',
    name: 'Music & Dance',
    description: 'Music taste, playlists, dance, singing, instruments, performing.',
  },
  { slug: 'nutrition', name: 'Nutrition', description: 'Food as fuel: what they eat to perform.' },
  {
    slug: 'outdoors_adventure',
    name: 'Outdoors & Adventure',
    description: 'Hiking, fishing, hunting, camping, boating, surfing, skiing for fun, nature.',
  },
  { slug: 'pets', name: 'Pets', description: 'Dogs, cats, or any animal as the subject.' },
  {
    slug: 'recovery_wellness',
    name: 'Recovery & Wellness',
    description: 'Recovery, health, and mental wellness.',
  },
  {
    slug: 'social_events',
    name: 'Social & Events',
    description: 'Going out: a specific occasion with friends or a crowd.',
  },
  {
    slug: 'sport_competition',
    name: 'Sport & Competition',
    description: "The athlete's own sport in action.",
  },
  {
    slug: 'sports_fandom_commentary',
    name: 'Sports Fandom & Commentary',
    description: 'Watching, reacting to, and talking about OTHER sports, games, and athletes.',
  },
  {
    slug: 'tech_gadgets',
    name: 'Tech & Gadgets',
    description: 'Phones, headphones, wearables, cameras, apps, and setups.',
  },
  {
    slug: 'training_fitness',
    name: 'Training & Fitness',
    description: 'Workouts and physical training, sport-specific or general.',
  },
  {
    slug: 'travel',
    name: 'Travel',
    description: 'Trips, destinations, and the experience of getting there.',
  },
] as const

export type ContentCategorySlug = (typeof CONTENT_CATEGORIES)[number]['slug']

export const CONTENT_CATEGORY_MAP: Record<string, (typeof CONTENT_CATEGORIES)[number]> =
  Object.fromEntries(CONTENT_CATEGORIES.map((c) => [c.slug, c]))

export const isContentCategory = (v: unknown): v is ContentCategorySlug =>
  typeof v === 'string' && v in CONTENT_CATEGORY_MAP
